import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../api/authService";
import {
  getToken,
  getUser,
  setToken,
  setUser,
  clearAuth,
  setRefreshToken,
  getRefreshToken,
} from "../../utils/auth";
import {
  getViewProfile as profileGetViewProfile,
  updateProfile as profileUpdateProfile,
} from "./profileSlice";
import socketService from "../../services/socketService";

// Action flags
export const ACTION_FLAGS = {
  LOGIN: "auth/login",
  REGISTER: "auth/register",
  REGISTER_STEP_ONE: "auth/registerStepOne",
  VERIFY_OTP_LOGIN_SIGNUP: "auth/verifyOtpLoginSignup",
  COMPLETE_PROFILE: "auth/completeProfile",
  LOGOUT: "auth/logout",
  FORGOT_PASSWORD: "auth/forgotPassword",
  RESET_PASSWORD: "auth/resetPassword",
  VERIFY_OTP: "auth/verifyOtp",
  RESEND_OTP: "auth/resendOtp",
  SOCIAL_LOGIN: "auth/socialLogin",
  GET_ROLES: "auth/getRoles",
  GET_EXPERIENCE: "auth/getExperience",
  GET_ALL_STATES: "auth/getAllStates",
  GET_ALL_CITIES: "auth/getAllCities",
  GET_LOCATION_CHOICE: "auth/getLocationChoice",
  GET_DELETE_ACCOUNT: "auth/deleteAccount",
};

// Initial state - Load from localStorage using auth utilities
const initialState = {
  user: getUser(),
  token: getToken(),
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  successMessage: "",
  actionFlag: "",
  // Registration form data - CORRECTED STRUCTURE
  roleItems: {
    describe_roles: [],
  },
  experienceItems: {
    nursing_experiences: [],
  },
  stateItems: {
    states: [],
  },
  cityItems: {
    cities: [],
  },
  locationChoiceItems: {
    location_choices: [],
  },
  deletItem: {},
};

// Async Thunks

// Login
export const loginUser = createAsyncThunk(
  ACTION_FLAGS.LOGIN,
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);

      // Store token, refresh token, and user data using auth utilities
      if (response.data.token) {
        setToken(response.data.token);
      }
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      if (response.data.data) {
        setUser(response.data.data);
      }

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Login failed";
      return rejectWithValue(message);
    }
  },
);

// Register
export const registerUser = createAsyncThunk(
  ACTION_FLAGS.REGISTER,
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);

      // Store token, refresh token, and user data if returned using auth utilities
      if (response.data.token) {
        setToken(response.data.token);
      }
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      if (response.data.data) {
        setUser(response.data.data);
      }

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Registration failed";
      return rejectWithValue(message);
    }
  },
);

// Logout
export const logoutUser = createAsyncThunk(
  ACTION_FLAGS.LOGOUT,
  async (_, { rejectWithValue }) => {
    try {
      // Attempt to call backend logout API
      await authService.logout();
      return null;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Logout failed";
      // Don't reject - we still want to clear local data
      console.warn("Logout API call failed, but clearing local data:", message);
      return null; // Return success even if API fails
    } finally {
      // ALWAYS clear auth data regardless of API success/failure.
      // Pass socketService so the authenticated websocket is also closed.
      clearAuth(socketService);
    }
  },
);

// Forgot Password
export const forgotPassword = createAsyncThunk(
  ACTION_FLAGS.FORGOT_PASSWORD,
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Request failed";
      return rejectWithValue(message);
    }
  },
);

// Reset Password
export const resetPassword = createAsyncThunk(
  ACTION_FLAGS.RESET_PASSWORD,
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(data);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Password reset failed";
      return rejectWithValue(message);
    }
  },
);

// Change Password
export const changePassword = createAsyncThunk(
  ACTION_FLAGS.CHANGE_PASSWORD,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await authService.changePassword(payload);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Password reset failed";
      return rejectWithValue(message);
    }
  },
);

// Verify OTP
export const verifyOtp = createAsyncThunk(
  ACTION_FLAGS.VERIFY_OTP,
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtp(data);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "OTP verification failed";
      return rejectWithValue(message);
    }
  },
);

// Register Step One
export const registerStepOne = createAsyncThunk(
  ACTION_FLAGS.REGISTER_STEP_ONE,
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.registerStepOne(userData);

      // Store token, refresh token, and user data if returned
      if (response.data.token) {
        setToken(response.data.token);
      }
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      if (response.data.data) {
        setUser(response.data.data);
      }

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Registration failed";
      return rejectWithValue(message);
    }
  },
);

// Verify OTP for Login/Signup
export const verifyOtpLoginSignup = createAsyncThunk(
  ACTION_FLAGS.VERIFY_OTP_LOGIN_SIGNUP,
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtpLoginSignup(data);

      // Store token, refresh token, and user data if returned
      if (response.data.token) {
        setToken(response.data.token);
      }
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      if (response.data.data) {
        setUser(response.data.data);
      }

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "OTP verification failed";
      return rejectWithValue(message);
    }
  },
);

// Complete Profile
export const completeProfile = createAsyncThunk(
  ACTION_FLAGS.COMPLETE_PROFILE,
  async (formData, { rejectWithValue }) => {
    try {
      const response = await authService.completeProfile(formData);

      // Check if token is at response.data.token or response.data.data.token
      const tokenFromResponse =
        response.data.token || response.data.data?.token;
      const refreshTokenFromResponse =
        response.data.refreshToken || response.data.data?.refreshToken;

      // Update token if backend returns a new permanent token
      if (tokenFromResponse) {
        setToken(tokenFromResponse);
      } else {
        console.warn(
          "No token found in response.data.token or response.data.data.token",
        );
      }

      // Update refresh token if returned
      if (refreshTokenFromResponse) {
        setRefreshToken(refreshTokenFromResponse);
      }

      // Update user data if returned
      if (response.data.data) {
        setUser(response.data.data);
      } else {
        console.warn(" No user data found in response.data.data");
      }

      return response.data;
    } catch (error) {
      console.error(" Complete profile error:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Profile completion failed";
      return rejectWithValue(message);
    }
  },
);

// Resend OTP
export const resendOtp = createAsyncThunk(
  ACTION_FLAGS.RESEND_OTP,
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.resendOtp(data);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Resend OTP failed";
      return rejectWithValue(message);
    }
  },
);

// Social Login
export const socialLogin = createAsyncThunk(
  ACTION_FLAGS.SOCIAL_LOGIN,
  async (socialData, { rejectWithValue }) => {
    try {
      const response = await authService.socialLogin(socialData);
      const isSignupComplete = (value) => {
        if (value === undefined || value === null) return false;
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return value === 1;
        if (typeof value === "string")
          return value === "1" || value.toLowerCase() === "true";
        return false;
      };

      const signupComplete = isSignupComplete(
        response?.data?.data?.is_complete_signup,
      );

      // Check if this is a new signup (no token) or existing user (has token)
      // User is considered existing if they have a token AND have completed signup
      if (response.data.token && signupComplete) {
        // Existing user - has token and completed signup
        setToken(response.data.token);

        // Store refresh token if available
        if (response.data.refreshToken) {
          setRefreshToken(response.data.refreshToken);
        }

        // Fetch complete user profile
        try {
          const profileResponse = await authService.viewProfile();

          if (profileResponse?.data?.data) {
            setUser(profileResponse.data.data);

            return {
              token: response.data.token,
              refreshToken: response.data.refreshToken,
              data: profileResponse.data.data,
              message: response.data.message || "Login successful",
              isNewUser: false,
              is_complete_signup: true,
            };
          }
        } catch (profileError) {
          console.warn("⚠️ ViewProfile failed, using social login data");
        }

        // Fallback if viewProfile fails
        if (response.data.data) {
          setUser(response.data.data);
        }

        return {
          ...response.data,
          isNewUser: false,
          is_complete_signup: true,
        };
      } else if (response.data.token && !signupComplete) {
        // User has token but hasn't completed signup - treat as returning incomplete user
        // Store token so they can complete registration
        setToken(response.data.token);

        return {
          ...response.data,
          configData: response?.config?.data,
          isNewUser: true,
          token: response.data.token,
        };
      } else {
        // New user - no token (signup)

        return {
          ...response.data,
          configData: response?.config?.data,
          isNewUser: true,
          token: null,
        };
      }
    } catch (error) {
      console.error("❌ Social login error:", error);
      const message =
        error.response?.data?.message || error.message || "Social login failed";
      return rejectWithValue(message);
    }
  },
);

// Get Roles
export const getRoles = createAsyncThunk(
  ACTION_FLAGS.GET_ROLES,
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getRoles();
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch roles";
      return rejectWithValue(message);
    }
  },
);

// Get Experience
export const getExperience = createAsyncThunk(
  ACTION_FLAGS.GET_EXPERIENCE,
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getExperience();
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch experience";
      return rejectWithValue(message);
    }
  },
);

// Get All States
export const getAllStates = createAsyncThunk(
  ACTION_FLAGS.GET_ALL_STATES,
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getAllStates();
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch states";
      return rejectWithValue(message);
    }
  },
);

// Get All Cities
export const getAllCities = createAsyncThunk(
  ACTION_FLAGS.GET_ALL_CITIES,
  async (params, { rejectWithValue }) => {
    try {
      const response = await authService.getAllCities(params);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch cities";
      return rejectWithValue(message);
    }
  },
);

// Get Location Choice
export const getLocationChoice = createAsyncThunk(
  ACTION_FLAGS.GET_LOCATION_CHOICE,
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getLocationChoice();
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch location choices";
      return rejectWithValue(message);
    }
  },
);

// Get Delete Account
export const deleteAccount = createAsyncThunk(
  ACTION_FLAGS.GET_DELETE_ACCOUNT,
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.deleteAccount();
      // Fully clear auth on delete. The previous code only removed "user" and
      // "authToken" and left "refreshToken" (and "lastRoute") behind, so stale
      // state could make the app look like the old account was still around.
      // clearAuth() removes token + refreshToken + user in one place.
      if (response?.data?.success) {
        clearAuth();
        localStorage.removeItem("lastRoute");
      }

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch delete account";
      return rejectWithValue(message);
    }
  },
);

// Refresh the current user from /view-profile. Updates BOTH localStorage
// (via setUser) and the redux state.auth.user (via the fulfilled handler).
// Use this anywhere the user record might have changed server-side and you
// need the UI to react immediately — Stripe success, cancel, plus-migration
// takeover, etc.
export const refreshUserProfile = createAsyncThunk(
  "auth/refreshUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.viewProfile();
      const data = response?.data?.data;
      if (data) setUser(data);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to refresh profile",
      );
    }
  },
);

// Auth Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Reset state - clears user and token as well
    resetAuthState: (state) => {
      state.user = null;
      state.token = null;
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.successMessage = "";
      state.actionFlag = "";
    },
    // Clear messages
    clearMessages: (state) => {
      state.errorMessage = "";
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    // Login cases
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
        state.actionFlag = "";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.data;
        state.token = action.payload.token;
        state.successMessage = action.payload.message || "Login successful";
        state.actionFlag = "LOGIN_SUCCESS";
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.user = null;
        state.token = null;
        state.actionFlag = "LOGIN_FAILED";
      })
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
        state.actionFlag = "";
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.data;
        state.token = action.payload.token;
        state.successMessage =
          action.payload.message || "Registration successful";
        state.actionFlag = "REGISTER_SUCCESS";
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "REGISTER_FAILED";
      })
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = null;
        state.token = null;
        state.successMessage = "Logout successful";
        state.actionFlag = "LOGOUT_SUCCESS";
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.errorMessage = action.payload;
        state.actionFlag = "LOGOUT_FAILED";
      })

      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Password reset link sent";
        state.actionFlag = "FORGOT_PASSWORD_SUCCESS";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "FORGOT_PASSWORD_FAILED";
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Password reset successful";
        state.actionFlag = "RESET_PASSWORD_SUCCESS";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "RESET_PASSWORD_FAILED";
      })
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Password reset successful";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "OTP verified successfully";
        state.actionFlag = "VERIFY_OTP_SUCCESS";
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "VERIFY_OTP_FAILED";
      })
      .addCase(registerStepOne.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(registerStepOne.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.data;
        state.token = action.payload.token;
        state.successMessage =
          action.payload.message || "Registration successful";
        state.actionFlag = "REGISTER_STEP_ONE_SUCCESS";
      })
      .addCase(registerStepOne.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "REGISTER_STEP_ONE_FAILED";
      })
      .addCase(verifyOtpLoginSignup.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(verifyOtpLoginSignup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.data;
        state.token = action.payload.token;
        state.successMessage =
          action.payload.message || "OTP verified successfully";
        state.actionFlag = "OTP_UPDT_SCS";
      })
      .addCase(verifyOtpLoginSignup.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "OTP_UPDT_FAILED";
      })
      .addCase(completeProfile.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(completeProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.data;

        // Extract token from either action.payload.token or action.payload.data.token
        const newToken = action.payload.token || action.payload.data?.token;

        // Update token in state if backend returns a new permanent token
        if (newToken) {
          state.token = newToken;
        } else {
          console.warn(
            "⚠️ No token found in action.payload.token or action.payload.data.token",
          );
        }

        state.successMessage =
          action.payload.message || "Profile completed successfully";
        state.actionFlag = "COMPLETE_PROFILE_SUCCESS";
      })
      .addCase(completeProfile.rejected, (state, action) => {
        console.error("=== COMPLETE PROFILE REDUCER (REJECTED) ===");
        console.error("Error:", action.payload);

        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "COMPLETE_PROFILE_FAILED";
      })
      .addCase(resendOtp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "OTP resent successfully";
        state.actionFlag = "RESEND_OTP_SUCCESS";
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "RESEND_OTP_FAILED";
      })
      .addCase(socialLogin.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
        state.actionFlag = "";
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        // Check if user is new (needs to complete registration) or existing (can go to dashboard)
        // isNewUser flag is set by the thunk based on is_complete_signup status
        if (action.payload.isNewUser) {
          // New user or user with incomplete signup - needs to complete registration
          state.token = action.payload.token || null; // May have token if returning incomplete user
          state.user = null;
          state.newUser = action.payload.data;
          state.successMessage =
            action.payload.message ||
            "Account created! Please complete your profile";
          state.actionFlag = "SOCIAL_SIGNUP_SUCCESS";
        } else {
          // Existing user with completed signup - redirect to dashboard
          state.token = action.payload.token;
          state.user = action.payload.data;
          state.newUser = null;
          state.successMessage = action.payload.message || "Login successful";
          state.actionFlag = "SOCIAL_LOGIN_SUCCESS";
        }
      })
      .addCase(socialLogin.rejected, (state, action) => {
        console.error("=== SOCIAL LOGIN REDUCER (REJECTED) ===");
        console.error("Error:", action.payload);

        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.user = null;
        state.token = null;
        state.actionFlag = "SOCIAL_LOGIN_FAILED";
      })
      .addCase(getRoles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both response structures
        if (action.payload.data && action.payload.data.describe_roles) {
          state.roleItems = action.payload.data;
        } else if (action.payload.describe_roles) {
          state.roleItems = action.payload;
        } else {
          state.roleItems = { describe_roles: action.payload };
        }
        state.actionFlag = "GET_ROLES_SUCCESS";
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "GET_ROLES_FAILED";
      })
      .addCase(getExperience.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getExperience.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both response structures
        if (action.payload.data && action.payload.data.nursing_experiences) {
          state.experienceItems = action.payload.data;
        } else if (action.payload.nursing_experiences) {
          state.experienceItems = action.payload;
        } else {
          state.experienceItems = { nursing_experiences: action.payload };
        }
        state.actionFlag = "GET_EXPERIENCE_SUCCESS";
      })
      .addCase(getExperience.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "GET_EXPERIENCE_FAILED";
      })
      .addCase(getAllStates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllStates.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both response structures
        if (action.payload.data && action.payload.data.states) {
          state.stateItems = action.payload.data;
        } else if (action.payload.states) {
          state.stateItems = action.payload;
        } else {
          state.stateItems = { states: action.payload };
        }
        state.actionFlag = "GET_ALL_STATES_SUCCESS";
      })
      .addCase(getAllStates.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "GET_ALL_STATES_FAILED";
      })
      .addCase(getAllCities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllCities.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both response structures
        if (action.payload.data && action.payload.data.cities) {
          state.cityItems = action.payload.data;
        } else if (action.payload.cities) {
          state.cityItems = action.payload;
        } else {
          state.cityItems = { cities: action.payload };
        }
        state.actionFlag = "GET_ALL_CITIES_SUCCESS";
      })
      .addCase(getAllCities.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "GET_ALL_CITIES_FAILED";
      })
      .addCase(getLocationChoice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getLocationChoice.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both response structures
        if (action.payload.data && action.payload.data.location_choices) {
          state.locationChoiceItems = action.payload.data;
        } else if (action.payload.location_choices) {
          state.locationChoiceItems = action.payload;
        } else {
          state.locationChoiceItems = { location_choices: action.payload };
        }
        state.actionFlag = "GET_LOCATION_CHOICE_SUCCESS";
      })
      .addCase(getLocationChoice.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.actionFlag = "GET_LOCATION_CHOICE_FAILED";
      })
      // Listen to profile slice actions to keep auth.user in sync
      .addCase(profileGetViewProfile.fulfilled, (state, action) => {
        // Sync profile data to auth.user
        if (action.payload.profileItem?.data) {
          state.user = { ...state.user, ...action.payload.profileItem.data };
        }
      })
      .addCase(profileUpdateProfile.fulfilled, (state, action) => {
        // Sync profile data to auth.user
        if (action.payload.profileItem?.data) {
          state.user = { ...state.user, ...action.payload.profileItem.data };
        }
      })
      .addCase(deleteAccount.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = null;
        state.token = null;
        state.successMessage = "Delete Account successful";
        state.actionFlag = "DELETE_ACCOUNT_SUCCESS";
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.errorMessage = action.payload;
        state.actionFlag = "DELETE_ACCOUNT_FAILED";
      })
      .addCase(refreshUserProfile.fulfilled, (state, action) => {
        // Mirrors what loginUser / completeProfile do: replace state.user
        // with the fresh /view-profile payload so all useSelector subscribers
        // re-render immediately.
        if (action.payload) {
          state.user = action.payload;
        }
      });
  },
});

export const { resetAuthState, clearMessages } = authSlice.actions;
export default authSlice.reducer;
